import type { Comparator } from '@domain/schemas/filters.schema';
import { countrySubdivisions } from '@root/helpers/schema/keywords/ensureSubDivisionBelongsToCountry';
import { isEnumOrCountrySubDivision } from '@root/helpers/schema/resolveSchemaTreeFromForm';
import type { SchemaTreeToFlatListResult } from '@root/helpers/schema/schemaTreeToFlatList';

import { TEXT_COMPARATORS } from '../constants';
import type { FieldDef, Option } from '../types';

import { CDR_SCHEMA } from './cdr-schema';

const RANGE_COMPARATORS: Comparator[] = ['eq', 'neq', 'gt', 'lt'];
const DATE_COMPARATORS: Comparator[] = ['eq', 'neq', 'gt', 'lt', 'rel'];
const ENUM_COMPARATORS: Comparator[] = ['eq', 'neq'];

let allSubdivisionOptionsCache: Option[] | undefined;

const getAllSubdivisionOptions = (): Option[] => {
  if (!allSubdivisionOptionsCache) {
    const names = new Set<string>();

    for (const entry of Object.values(countrySubdivisions) as Array<{ enum?: string[] }>) {
      for (const name of entry.enum ?? []) {
        names.add(name);
      }
    }

    allSubdivisionOptionsCache = [...names].sort((a, b) => a.localeCompare(b)).map((v) => ({ label: v, value: v }));
  }
  return allSubdivisionOptionsCache;
};

export const deriveFieldDef = (node: SchemaTreeToFlatListResult): FieldDef => {
  const isBinaryToggle = Boolean(node.enum && node['ui:component'] === 'InputToggleGroup');
  const isDate = Boolean(node.format?.includes('date'));
  const isNumber = node.type === 'number' || node.type === 'integer';
  const isEnumLike = isEnumOrCountrySubDivision(node as Pick<typeof node, 'enum' | 'ui:component' | '$ref'>);

  if (isEnumLike || isBinaryToggle) {
    let options: Option[] =
      node['ui:component'] === 'InputCountrySubDivision'
        ? getAllSubdivisionOptions()
        : (node.enum?.map((v) => ({ label: v, value: v })) ?? []);
    if (options.length === 0 && node.$ref) {
      const definition =
        CDR_SCHEMA.definitions[node.$ref.split('#/definitions/')[1] as keyof typeof CDR_SCHEMA.definitions];
      if (definition?.enum) {
        options = options.concat(definition.enum.map((v: string) => ({ label: v, value: v })));
      }
    }
    return {
      id: node.id,
      label: node.title,
      comparators: ENUM_COMPARATORS,
      valueInput: 'EnumMultiSelect',
      ...(options.length > 0 ? { options } : {}),
    };
  }

  if (isDate) {
    return { id: node.id, label: node.title, comparators: DATE_COMPARATORS, valueInput: 'DateInput' };
  }

  if (isNumber) {
    const isPercentage = node['ui:component'] === 'InputPercentage' || node.id.endsWith('_percentage');
    const isAmount = node['ui:component'] === 'InputCurrency' || node.id.endsWith('.amount');

    const numberFormat = isPercentage
      ? { thousandSeparator: false, decimalScale: 7, suffix: '%' }
      : isAmount
        ? { thousandSeparator: true, decimalScale: 2 }
        : { thousandSeparator: false, decimalScale: 0 };

    return { id: node.id, label: node.title, comparators: RANGE_COMPARATORS, valueInput: 'NumberInput', numberFormat };
  }

  return { id: node.id, label: node.title, comparators: TEXT_COMPARATORS, valueInput: 'TextInput' };
};
